import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { useMemo } from 'react';
import type { ApiPPG, MetricValue } from '../../../../types/promoMetrics';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import { buildPromoFormValues } from '../../../../utils/buildPromoPayload';

interface PromoApiPPG {
    ppg_id: string;
    promotion_id: string;
    event_id: string;
    plan_id: string;
    dim1_id: number;
    dim1_name: string;
    metrics: Record<string, MetricValue>;
}

interface PromoApiResponse {
    promotion_id: string;
    event_id: string;
    plan_id: string;
    attributes: Record<string, unknown>;
    name: string;
    start_date: string;
    end_date: string;
    cra_start_date: string;
    cra_end_date: string;
    status: string;
    offer: { offer_type: string } | null;
    ppgs: PromoApiPPG[];
}

const fetchPromoById = async (promotionId: string, signal?: AbortSignal) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.getPromoById(promotionId), {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });
    return response.data as PromoApiResponse;
};

const useGetPromoData = (promotionId: string | null) => {
    const { promoFields, entityAttributes, fieldRules } = useTpoGenConfig();
    const { data, isPending, isFetching, isError } = useQuery({
        queryKey: tpoGenKeys.promoDetails(promotionId ?? ''),
        queryFn: ({ signal }) => fetchPromoById(promotionId as string, signal),
        enabled: !!promotionId,
    });

    const promoFormValues = useMemo(() => {
        if (!data) return null;
        return buildPromoFormValues(
            entityAttributes,
            promoFields,
            data as unknown as Record<string, unknown>,
            fieldRules,
        );
    }, [data, promoFields, entityAttributes, fieldRules]);

    const ppgData: ApiPPG[] = useMemo(() => {
        if (!data?.ppgs) return [];
        return data.ppgs.map((ppg) => ({
            ppg_id: ppg.dim1_id,
            ppg_name: ppg.dim1_name,
            metrics: ppg.metrics,
        }));
    }, [data]);

    const ppgIds: number[] = useMemo(() => {
        if (!data?.ppgs) return [];
        return data.ppgs.map((ppg) => ppg.dim1_id);
    }, [data]);

    const promoMeta = useMemo(() => {
        if (!data) return null;
        return {
            promotion_id: data.promotion_id,
            event_id: data.event_id,
            plan_id: data.plan_id,
            status: data.status,
        };
    }, [data]);

    return {
        promoFormValues,
        ppgData,
        ppgIds,
        promoMeta,
        isPending: !!promotionId && isPending,
        isFetching,
        isError,
    };
};

export default useGetPromoData;
